import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Account } from "./AccountService";
import { User } from "./UserService";

// GraphQL queries
const ROOM_TYPE_LIST_QUERY = gql`
  query RoomTypeList($filter: RoomTypeFilter, $params: PaginationAndSorting) {
    roomTypeList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        name
        description
        color
        icon
        orderNo
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ROOM_TYPE_GET_QUERY = gql`
  query RoomTypeGet($id: ID) {
    roomTypeGet(id: $id) {
      code
      data {
        id
        accountId
        name
        description
        color
        icon
        orderNo
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ROOM_TYPE_GET_MANY_QUERY = gql`
  query RoomTypeGetMany($ids: [ID]) {
    roomTypeGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        name
        description
        color
        icon
        orderNo
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const ROOM_TYPE_CREATE_MUTATION = gql`
  mutation RoomTypeCreate($params: RoomTypeInput) {
    roomTypeCreate(params: $params) {
      code
      data {
        id
        accountId
        name
        description
        color
        icon
        orderNo
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ROOM_TYPE_UPDATE_MUTATION = gql`
  mutation RoomTypeUpdate($where: RoomTypeWhereInput, $params: RoomTypeInput) {
    roomTypeUpdate(where: $where, params: $params) {
      code
      data {
        id
        accountId
        name
        description
        color
        icon
        orderNo
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ROOM_TYPE_REMOVE_MUTATION = gql`
  mutation RoomTypeRemove($where: RoomTypeWhereInput) {
    roomTypeRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface RoomType {
  id: string;
  accountId?: string;
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  orderNo?: number;
  status?: number;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  userCreated?: User;
  userUpdated?: User;
  account?: Account;
}

export interface RoomTypeFilter {
  id?: string[];
  accountId?: string;
  name?: string;
  status?: number[];
}

export interface RoomTypeInput {
  accountId?: string;
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  orderNo?: number;
  status?: number;
}

export interface RoomTypeWhereInput {
  id: string;
}

/**
 * Room Types service that handles all room type-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove
 */
export class RoomTypesService {
  /**
   * Get a list of room types with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: RoomTypeFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(ROOM_TYPE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single room type by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(ROOM_TYPE_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple room types by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(ROOM_TYPE_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new room type
   */
  static async create(params: RoomTypeInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(ROOM_TYPE_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing room type
   */
  static async update(id: string, params: RoomTypeInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(ROOM_TYPE_UPDATE_MUTATION, {
      where: { id },
      params,
    });
    return result;
  }

  /**
   * Remove/delete a room type
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(ROOM_TYPE_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }
}
